import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "border-b border-gray-200 bg-white shadow-sm" }
const _hoisted_2 = { class: "mx-auto flex w-full max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:px-8" }
const _hoisted_3 = { class: "flex items-center justify-between gap-3" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", { class: "block text-xs font-normal text-gray-500" }, "Кабинет менеджера", -1)
const _hoisted_5 = { class: "flex items-center gap-2" }
const _hoisted_6 = {
  key: 0,
  class: "absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-xs font-bold text-white"
}
const _hoisted_7 = {
  key: 0,
  class: "rounded-xl border border-red-200 bg-red-50 p-3"
}
const _hoisted_8 = {
  key: 0,
  class: "text-sm text-gray-600"
}
const _hoisted_9 = {
  key: 1,
  class: "space-y-2"
}
const _hoisted_10 = { class: "min-w-0 text-sm" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", { class: "font-bold text-red-700" }, " ⚠️ Запуск мимо кассы ", -1)
const _hoisted_12 = { class: "text-gray-800" }
const _hoisted_13 = ["disabled", "onClick"]
const _hoisted_14 = {
  key: 2,
  class: "mt-2 border-t border-red-100 pt-2 text-xs text-gray-500"
}
const _hoisted_15 = {
  "aria-label": "Основная навигация",
  class: "flex flex-wrap gap-2 text-sm"
}

import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import type ICurrentManager from "@/interfaces/ICurrentManager";
import {canManageEmployees, getCurrentAccount} from "@/services/account.service";
import {logout} from "@/services/auth.service";
import machineAlertService from "@/services/machine_alert.service";
import AssistantPanel from "@/components/blocks/AssistantPanel.vue";

const ALERTS_POLL_MSEC = 60 * 1000;

type MachineAlertRow = {
  id: number;
  shop_id: number;
  shop_name?: string | null;
  machine_index: number;
  type: string;
  created_at: string;
};

// Название точки вместо ID («Московский» вместо «точка 17»).

export default /*#__PURE__*/_defineComponent({
  __name: 'CabinetHeader',
  setup(__props) {

const router = useRouter();
const account = ref<ICurrentManager | null>(null);

// ИИ-ассистент: панель открывается кнопкой в шапке, доступен только владельцу
const assistantOpen = ref(false);
const isOwner = computed(() => account.value?.role === "owner");

// А-Ф: непринятые тревоги детектора (poll раз в 60 с, лёгкий запрос).
const shopTitle = (alert: MachineAlertRow) =>
  alert.shop_name ? alert.shop_name : `точка ${alert.shop_id}`;

const alerts = ref<MachineAlertRow[]>([]);
const alertsOpen = ref(false);
const ackingId = ref<number | null>(null);
let alertsTimer: number | null = null;

const canAckAlerts = computed(() => canManageEmployees(account.value));

const summaryText = ref("");

const loadAlerts = async () => {
  try {
    const response = await machineAlertService.unacked();
    const list = response?.data?.result?.alerts;
    alerts.value = Array.isArray(list) ? list : [];
  } catch (error) {
    // тревоги не должны ломать шапку; сеть моргнула - покажем в следующий poll
    console.log("loadAlerts error:", error);
  }
  try {
    const response = await machineAlertService.summary();
    const summary = response?.data?.result;
    const parts: string[] = [];
    if (summary?.late) parts.push(`поздние активации — ${summary.late}`);
    if (summary?.second_run_soft) parts.push(`вторые заходы — ${summary.second_run_soft}`);
    summaryText.value = parts.join(", ");
  } catch (error) {
    console.log("loadSummary error:", error);
  }
};

const ackAlert = async (id: number) => {
  if (ackingId.value !== null) return;
  ackingId.value = id;
  try {
    await machineAlertService.ack(id);
    alerts.value = alerts.value.filter((a) => a.id !== id);
  } catch (error) {
    console.log("ackAlert error:", error);
  } finally {
    ackingId.value = null;
  }
};

const formatAlertTime = (value: string) => {
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value;
  const d = new Date(ts);
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mo} ${hh}:${mi}`;
};
const items = [
  {title: "Главная", to: "/dashboard"},
  {title: "Задачи", to: "/tasks", requiresEmployeeAdmin: true},
  {title: "Статистика", to: "/statistics"},
  {title: "Удалённый запуск", to: "/remote-machine-start"},
  {title: "Касса", to: "/cashbox", requiresEmployeeAdmin: true},
  {title: "Учёт", to: "/finance", requiresEmployeeAdmin: true},
  {title: "Архив", to: "/archive", requiresEmployeeAdmin: true},
  {title: "Клиенты", to: "/clients", requiresEmployeeAdmin: true},
  {title: "Сотрудники", to: "/employees", requiresEmployeeAdmin: true}
];

const visibleItems = computed(() => items.filter((item) =>
  !item.requiresEmployeeAdmin || canManageEmployees(account.value)
));

const signOut = async () => {
  logout();
  await router.replace({name: "login"});
};

onMounted(async () => {
  try {
    account.value = await getCurrentAccount();
  } catch (error) {
    account.value = null;
  }
  void loadAlerts();
  alertsTimer = window.setInterval(() => {
    void loadAlerts();
  }, ALERTS_POLL_MSEC);
});

onBeforeUnmount(() => {
  if (alertsTimer !== null) window.clearInterval(alertsTimer);
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("header", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_router_link, {
            to: "/dashboard",
            class: "text-left font-bold text-gray-900"
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Antilaundry "),
              _hoisted_4
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_5, [
            (isOwner.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "rounded-md px-3 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-indigo-500",
                  title: "ИИ-ассистент по учёту и терминалам",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (assistantOpen.value = true))
                }, " 🤖 Ассистент "))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["relative rounded-md px-3 py-2 text-lg leading-none hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500", alerts.value.length ? 'text-red-600' : 'text-gray-400']),
              title: "Тревоги",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (alertsOpen.value = !alertsOpen.value))
            }, [
              _createTextVNode(" 🔔 "),
              (alerts.value.length)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(alerts.value.length), 1))
                : _createCommentVNode("", true)
            ], 2),
            _createElementVNode("button", {
              type: "button",
              class: "rounded-md px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500",
              onClick: signOut
            }, " Выйти ")
          ])
        ]),
        (alertsOpen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (!alerts.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, " Тревог нет. "))
                : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(alerts.value, (alert) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: alert.id,
                        class: "flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 shadow-sm"
                      }, [
                        _createElementVNode("div", _hoisted_10, [
                          _hoisted_11,
                          _createElementVNode("span", _hoisted_12, " — машина №" + _toDisplayString(alert.machine_index) + ", " + _toDisplayString(shopTitle(alert)) + ", " + _toDisplayString(formatAlertTime(alert.created_at)), 1)
                        ]),
                        (canAckAlerts.value)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              type: "button",
                              class: "rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50",
                              disabled: ackingId.value === alert.id,
                              onClick: ($event: any) => (ackAlert(alert.id))
                            }, _toDisplayString(ackingId.value === alert.id ? "…" : "Принято"), 9, _hoisted_13))
                          : _createCommentVNode("", true)
                      ]))
                    }), 128))
                  ])),
              (summaryText.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, " Сводка за сутки: " + _toDisplayString(summaryText.value), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("nav", _hoisted_15, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleItems.value, (item) => {
            return (_openBlock(), _createBlock(_component_router_link, {
              key: item.to,
              to: item.to,
              class: "rounded-md px-3 py-2 font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900",
              "active-class": "bg-indigo-50 text-indigo-700"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(item.title), 1)
              ]),
              _: 2
            }, 1032, ["to"]))
          }), 128))
        ])
      ])
    ]),
    _createVNode(AssistantPanel, {
      open: assistantOpen.value,
      onClose: _cache[2] || (_cache[2] = ($event: any) => (assistantOpen.value = false))
    }, null, 8, ["open"])
  ], 64))
}
}

})